import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, ArrowLeft, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';

interface NewOrderFormProps {
  onClose: () => void;
  onSubmit: (orderData: any) => void;
}

const NewOrderForm: React.FC<NewOrderFormProps> = ({ onClose, onSubmit }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [orderData, setOrderData] = useState({
    garmentType: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    specialInstructions: '',
    hasMeasurements: false,
    measurements: {},
    photos: [],
    selectedAppointment: null,
  });

  const garmentTypes = [
    { id: 'suit', name: 'Suit', icon: '👔' },
    { id: 'dress', name: 'Dress', icon: '👗' },
    { id: 'shirt', name: 'Shirt', icon: '👕' },
    { id: 'pants', name: 'Pants', icon: '👖' },
    { id: 'blazer', name: 'Blazer', icon: '🧥' },
    { id: 'kurta', name: 'Kurta', icon: '🥻' },
    { id: 'sherwani', name: 'Sherwani', icon: '👘' },
    { id: 'lehenga', name: 'Lehenga', icon: '👗' },
  ];

  const priorities = [
    { id: 'low', name: 'Low', color: '#10B981', days: 21 },
    { id: 'medium', name: 'Medium', color: '#F59E0B', days: 14 },
    { id: 'high', name: 'High', color: '#EF4444', days: 7 },
  ];

  // Load preselected appointment from localStorage
  useEffect(() => {
    const selectedAppointmentData = localStorage.getItem('selectedAppointment');
    if (selectedAppointmentData) {
      try {
        const appointmentData = JSON.parse(selectedAppointmentData);
        console.log('Loaded preselected appointment:', appointmentData);
        
        // Set the selected appointment and skip to the appointment step
        setOrderData(prev => ({
          ...prev,
          selectedAppointment: {
            id: appointmentData.slotId,
            date: appointmentData.appointmentDate,
            start_time: appointmentData.appointmentTime,
            end_time: appointmentData.appointmentEndTime,
            tailor_id: appointmentData.tailorId,
            tailor_name: appointmentData.tailorName
          }
        }));
        
        // Start from the beginning (step 1) but with preselected appointment
        setCurrentStep(1);
        
        // Clear the localStorage data after loading
        localStorage.removeItem('selectedAppointment');
      } catch (_error) {
        console.error('Error parsing selected appointment data:', error);
      }
    }
  }, []);

  const handleGarmentSelect = (garment: any) => {
    setOrderData({
      ...orderData,
      garmentType: garment.id,
    });
    setCurrentStep(2);
  };

  const handlePrioritySelect = (priority: any) => {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + priority.days);
    
    setOrderData({
      ...orderData,
      priority: priority.id,
      dueDate: dueDate.toISOString().split('T')[0],
    });
    setCurrentStep(3);
  };

  const handleMeasurementsToggle = (hasMeasurements: boolean) => {
    setOrderData({
      ...orderData,
      hasMeasurements,
    });
    // Don't auto-advance - let user upload files or click Next
  };

  const handleAppointmentSelect = (appointment: any) => {
    setOrderData({
      ...orderData,
      selectedAppointment: appointment,
    });
    setCurrentStep(5);
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1: return orderData.garmentType !== '';
      case 2: return orderData.priority !== '';
      case 3: return true; // Measurements step - always can proceed
      case 4: return orderData.selectedAppointment !== null; // Appointment step - must select slot
      case 5: return true; // Final step, can always proceed to submit
      default: return false;
    }
  };

  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const apiUrl = API_BASE;
      const token = localStorage.getItem('authToken');
      const authHeaders = { Authorization: `Bearer ${token}` };

      let response = await fetch(`${apiUrl}/api/appointments/calendar-slots?status=available`, {
        headers: authHeaders,
      });

      if (!response.ok) {
        response = await fetch(`${apiUrl}/api/test/calendar-slots`, { headers: authHeaders });
      }
      
      if (response.ok) {
        const slots = await response.json();
        setAvailableSlots(slots);
      } else {
        // No available slots - show empty
        setAvailableSlots([]);
      }
    } catch (_error) {
      console.error('Failed to fetch appointment slots:', error);
      // No available slots - show empty
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleNext = () => {
    if (!canProceedToNext()) {
      // Show validation message based on current step
      const messages = {
        1: 'Please select a garment type to continue.',
        2: 'Please select a priority to continue.',
        4: 'Please select an appointment slot to continue.',
      };
      alert(messages[currentStep as keyof typeof messages]);
      return;
    }
    
    // Fetch appointment slots when moving to step 4
    if (currentStep === 3) {
      fetchAvailableSlots();
    }
    
    setCurrentStep(currentStep + 1);
  };

  const handleSubmit = async () => {
    if (!orderData.garmentType) {
      alert('Please fill in all required fields.');
      return;
    }

    const selectedGarment = garmentTypes.find(g => g.id === orderData.garmentType);
    const selectedPriority = priorities.find(p => p.id === orderData.priority);
    
    const finalOrderData = {
      ...orderData,
      garmentName: selectedGarment?.name,
      priorityName: selectedPriority?.name,
      orderNumber: `ORD-${Date.now()}`,
      status: 'pending',
      createdAt: new Date().toISOString(),
    };

    try {
      // Submit to backend API
      const apiUrl = API_BASE;

      const token = localStorage.getItem('authToken');
      
      // Use authenticated endpoint to ensure order is associated with current user
      const response = await fetch(`${apiUrl}/api/test/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`  // Add authentication
        },
        body: JSON.stringify({
          id: finalOrderData.orderNumber,
          garment_type: finalOrderData.garmentName,
          description: finalOrderData.description || `${finalOrderData.garmentName} - Details to be discussed at appointment`,
          status: 'pending',
          order_date: new Date().toISOString().split('T')[0],
          due_date: finalOrderData.dueDate,
          priority: finalOrderData.priority,
          special_instructions: finalOrderData.specialInstructions,
          customer_email: user?.email || 'customer@masterji.com',
          has_measurements: finalOrderData.hasMeasurements,
          measurements: finalOrderData.measurements,
          photos: finalOrderData.photos,
          progress_steps: [
            { step: 'Order Placed', status: 'completed', date: new Date().toISOString().split('T')[0] },
            { step: 'Measurements Required', status: 'pending', date: null },
            { step: 'Fabric Selection', status: 'pending', date: null },
            { step: 'Cutting', status: 'pending', date: null },
            { step: 'Stitching', status: 'pending', date: null },
            { step: 'Quality Check', status: 'pending', date: null },
            { step: 'Ready for Pickup', status: 'pending', date: null }
          ]
        }),
      });

      if (response.ok) {
        alert('Order created successfully! You will need to schedule an appointment for measurements and pricing.');
        onSubmit(finalOrderData);
        onClose();
        setCurrentStep(1);
        setOrderData({
          garmentType: '',
          fabricType: '',
          color: '',
          description: '',
          priority: 'medium',
          dueDate: '',
          specialInstructions: '',
        });
      } else {
        const errorData = await response.json();
        alert(`Order creation failed: ${errorData.detail || 'Please try again.'}`);
      }
    } catch (_error) {
      console.error('Order creation error:', error);
      alert('Order creation failed. Please check your connection and try again.');
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Garment Type <span className="text-red-500">*</span></h2>
        <p className="text-gray-600">What would you like to get tailored?</p>
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> Pricing will be quoted at your appointment after measurements and fabric selection.
          </p>
        </div>
        {!orderData.garmentType && (
          <p className="text-red-500 text-sm mt-2">Please select a garment type to continue</p>
        )}
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {garmentTypes.map((garment) => (
          <motion.button
            key={garment.id}
            className={`p-4 rounded-lg border-2 transition-all ${
              orderData.garmentType === garment.id
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            onClick={() => handleGarmentSelect(garment)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="text-3xl mb-2">{garment.icon}</div>
            <div className="font-semibold text-gray-900">{garment.name}</div>
          </motion.button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Set Priority <span className="text-red-500">*</span></h2>
        <p className="text-gray-600">How quickly do you need this order?</p>
        {!orderData.priority && (
          <p className="text-red-500 text-sm mt-2">Please select a priority to continue</p>
        )}
      </div>
      
      <div className="space-y-3">
        {priorities.map((priority) => (
          <motion.button
            key={priority.id}
            className={`w-full p-4 rounded-lg border-2 transition-all flex items-center ${
              orderData.priority === priority.id
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
            onClick={() => handlePrioritySelect(priority)}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <div 
              className="w-3 h-3 rounded-full mr-3"
              style={{ backgroundColor: priority.color }}
            />
            <div className="flex-1 text-left">
              <div className="font-semibold text-gray-900">{priority.name}</div>
              <div className="text-sm text-gray-600">{priority.days} days</div>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Measurements & Photos</h2>
        <p className="text-gray-600">Do you have existing measurements to upload?</p>
      </div>
      
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Important Note</h3>
              <p className="text-sm text-blue-700 mt-1">
                You will still need to visit the tailor for precise measurements and standardized photos. 
                This step is optional and helps speed up the process.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <motion.button
            onClick={() => handleMeasurementsToggle(true)}
            className={`p-6 rounded-lg border-2 transition-all text-left ${
              orderData.hasMeasurements === true
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="text-center">
              <div className="text-lg mb-2 font-bold text-blue-600">MEASUREMENTS</div>
              <div className="font-semibold">Yes, I have measurements</div>
              <div className="text-sm text-gray-600 mt-1">Upload existing measurements</div>
            </div>
          </motion.button>

          <motion.button
            onClick={() => handleMeasurementsToggle(false)}
            className={`p-6 rounded-lg border-2 transition-all text-left ${
              orderData.hasMeasurements === false
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="text-center">
              <div className="text-3xl mb-2">📝</div>
              <div className="font-semibold">No, I'll get measured</div>
              <div className="text-sm text-gray-600 mt-1">Skip to appointment</div>
            </div>
          </motion.button>
        </div>

        {orderData.hasMeasurements && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-3">Upload Measurements (Optional)</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white rounded border">
                <span className="text-sm text-gray-600">Measurement Form (PDF/Image)</span>
                <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                  Upload
                </button>
              </div>
              <div className="flex items-center justify-between p-3 bg-white rounded border">
                <span className="text-sm text-gray-600">Standardized Photos (Front, Side, Back)</span>
                <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                  Upload
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              * Photos will be taken during your appointment regardless
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Book Appointment <span className="text-red-500">*</span></h2>
        <p className="text-gray-600">Select an available time slot for your measurements</p>
        {!orderData.selectedAppointment && (
          <p className="text-red-500 text-sm mt-2">Please select an appointment slot to continue</p>
        )}
      </div>
      
      {/* Show preselected appointment if it exists */}
      {orderData.selectedAppointment ? (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border-2 border-green-500 bg-green-50 text-green-700">
            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold">
                  {new Date(orderData.selectedAppointment.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
                <div className="text-sm text-gray-600">
                  {orderData.selectedAppointment.start_time} - {orderData.selectedAppointment.end_time}
                </div>
                {orderData.selectedAppointment.tailor_name && (
                  <div className="text-sm text-gray-500 mt-1">
                    with {orderData.selectedAppointment.tailor_name}
                  </div>
                )}
              </div>
              <div className="text-sm font-medium text-green-600">
                Selected ✓
              </div>
            </div>
          </div>
          <div className="text-center text-sm text-gray-500">
            This appointment was preselected from the appointments page
          </div>
        </div>
      ) : loadingSlots ? (
        <div className="flex justify-center py-8">
          <div className="text-gray-600">Loading available slots...</div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3">
            {availableSlots.map((slot: any) => (
              <motion.button
                key={slot.id}
                onClick={() => handleAppointmentSelect(slot)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  orderData.selectedAppointment?.id === slot.id
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-semibold">
                      {new Date(slot.date).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                    <div className="text-sm text-gray-600">
                      {slot.start_time} - {slot.end_time}
                    </div>
                    <div className="text-sm text-gray-500">
                      {slot.tailor?.name || 'Master Tailor Studio'}
                    </div>
                  </div>
                  <div className="text-sm font-bold text-green-600">SLOT</div>
                </div>
              </motion.button>
            ))}
          </div>
          
          {availableSlots.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <div className="text-lg mb-2 font-bold text-gray-600">NO SLOTS</div>
              <div>No available slots found</div>
              <div className="text-sm mt-1">Please contact us to schedule an appointment</div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Additional Details</h2>
        <p className="text-gray-600">Add any special instructions</p>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (Optional)
          </label>
          <textarea
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Describe your requirements..."
            value={orderData.description}
            onChange={(e) => setOrderData({ ...orderData, description: e.target.value })}
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Instructions (Optional)
          </label>
          <textarea
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Any specific requirements..."
            value={orderData.specialInstructions}
            onChange={(e) => setOrderData({ ...orderData, specialInstructions: e.target.value })}
            rows={3}
          />
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Garment:</span>
            <span className="font-medium">{garmentTypes.find(g => g.id === orderData.garmentType)?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Priority:</span>
            <span className="font-medium">{priorities.find(p => p.id === orderData.priority)?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Due Date:</span>
            <span className="font-medium">{new Date(orderData.dueDate).toLocaleDateString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Measurements:</span>
            <span className="font-medium">{orderData.hasMeasurements ? 'Will upload' : 'At appointment'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Photos:</span>
            <span className="font-medium">Required at appointment</span>
          </div>
          {orderData.selectedAppointment && (
            <div className="flex justify-between">
              <span className="text-gray-600">Appointment:</span>
              <span className="font-medium">
                {new Date(orderData.selectedAppointment.date).toLocaleDateString()} at {orderData.selectedAppointment.start_time}
              </span>
            </div>
          )}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Next Steps Required</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>• Schedule an appointment for precise measurements</p>
                  <p>• Get accurate pricing quote from your tailor</p>
                  <p>• Finalize fabric selection and design details</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return renderStep1();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-semibold text-gray-900">New Order</h1>
          <div className="w-9" />
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 5) * 100}%` }}
            />
          </div>
          <p className="text-sm text-gray-600 text-center mt-2">Step {currentStep} of 5</p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {renderCurrentStep()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          {currentStep > 1 && (
            <button
              onClick={() => setCurrentStep(currentStep - 1)}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>
          )}
          
          <div className="flex-1" />
          
          <button
            onClick={currentStep === 5 ? handleSubmit : handleNext}
            className={`flex items-center px-6 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 5
                ? 'bg-green-600 text-white hover:bg-green-700'
                : canProceedToNext()
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-400 text-white cursor-not-allowed'
            }`}
            disabled={currentStep < 5 && !canProceedToNext()}
          >
            {currentStep === 5 ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Place Order
              </>
            ) : (
              <>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default NewOrderForm;
